import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import Panorama from "components/field/Panorama";
import { useCaptureFrames } from "hooks/useCaptureFrames";
import { rtctApi } from "services/rtctApi";
import { calibrationForIdentities, cleanCalibration, defaultCalibration, eligibleCaptures, fieldTime } from "utils/fieldFormat";
import { useStableState } from "utils/stableState";

// Photos on the node page: the newest capture in Field cameras' own 360 view (Panorama, with the node's saved lens
// settings: flips, yaw, field of view), small and draggable (GET frames/ and calibration/, the Field cameras page's
// own sources; the images load through useCaptureFrames). Playback, zoom and the lens view live on Field cameras.
const POLL_MS = 5 * 60 * 1000;

export function newestCapture(data) {
  const captures = Array.isArray(data?.captures) ? data.captures : [];
  return (
    captures
      .filter((c) => Array.isArray(c?.cameras) && c.cameras.length)
      .sort((a, b) => Date.parse(b.captured_at || 0) - Date.parse(a.captured_at || 0))[0] || null
  );
}

export default function LatestPhotos({ mac }) {
  const [state, setState] = useStableState({ mac: "", captures: [], count: null, calibration: null, error: "" });
  useEffect(() => {
    if (!mac || typeof rtctApi.frames !== "function") return undefined;
    let abort = new AbortController();
    const load = () => {
      abort.abort();
      abort = new AbortController();
      const signal = abort.signal;
      const calibration =
        typeof rtctApi.calibration === "function"
          ? Promise.resolve(rtctApi.calibration(mac, signal)).catch(() => null)
          : Promise.resolve(null);
      Promise.all([Promise.resolve(rtctApi.frames(mac, signal)), calibration])
        .then(([data, cal]) => {
          if (!signal.aborted)
            setState({
              mac,
              captures: Array.isArray(data?.captures) ? data.captures : [],
              count: Number.isFinite(data?.count) ? data.count : null,
              calibration: cal && typeof cal === "object" ? cal : null,
              error: "",
            });
        })
        .catch((e) => {
          if (!signal.aborted) setState((old) => ({ ...old, mac, error: e?.message || "Photos unavailable" }));
        });
    };
    load();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => {
      clearInterval(timer);
      abort.abort();
    };
  }, [mac, setState]);
  const mine = state.mac === mac;
  const newest = useMemo(() => {
    const capture = newestCapture({ captures: mine ? state.captures : [] });
    return capture ? eligibleCaptures([capture]) : [];
  }, [mine, state.captures]);
  const display = useCaptureFrames(newest, 0);
  const frame = display.frame;
  const saved = mine ? state.calibration : null;
  const view = useMemo(() => {
    const shownIds = Object.keys(frame?.images || {}).sort();
    const savedIds = Object.keys(saved?.cameras || {}).sort();
    // The saved lens settings when they name this capture's cameras, else the defaults for them.
    const cal =
      savedIds.length && savedIds.every((id) => !shownIds.length || shownIds.includes(id))
        ? cleanCalibration({ ...defaultCalibration(), ...saved })
        : calibrationForIdentities(shownIds.length ? shownIds : ["A", "B"]);
    const ids = Object.keys(cal.cameras).sort();
    return {
      images: { A: frame?.images?.[ids[0]], B: frame?.images?.[ids[1]] },
      calibration: {
        ...cal,
        cameras: { A: cal.cameras[ids[0]], B: cal.cameras[ids[1]] || defaultCalibration().cameras.B },
      },
    };
  }, [frame, saved]);
  const viewRef = useRef(null);
  const capture = newest[0] || null;
  return (
    <section className="field-panel" aria-label="Photos">
      <div className="field-panel-head">
        <div>
          <h2>Photos</h2>
          <p>{capture ? `Latest pair, ${fieldTime(capture.captured_at)} SGT` : "Latest pair"}</p>
        </div>
        {state.count !== null && mine && <span className="field-state">{state.count} captures</span>}
      </div>
      <div className="field-panel-body">
        {state.error && mine && <div className="field-alert">{state.error}</div>}
        {!capture && !state.error && <div className="field-notice">No photo yet.</div>}
        {capture && (
          <div className="field-viewer field-node-viewer" data-testid="node-panorama">
            <Panorama images={view.images} calibration={view.calibration} resetKey={0} viewRef={viewRef} describedBy="node-panorama-help" />
          </div>
        )}
        <p id="node-panorama-help" className="field-caption">
          {capture ? "Drag to look around. " : ""}All photos in <Link to="/field-cameras">Field cameras</Link>.
        </p>
      </div>
    </section>
  );
}
